// ============================================================================
// Safe Care - Rapport statistique (/assurance/rapport)
// Module : features/assistance_sociale/pages
//
// Tableau de bord agrégé alimenté par Souscription/GetStatistiques
// (sp_SafeCare_Statistiques). Le diagramme circulaire de répartition des
// souscriptions par statut est affiché en tout premier.
// Charts : recharts (même style que Components/Dashbord.tsx).
// ============================================================================

import { useMemo, useState } from 'react';
import { Box, Grid, Paper, Typography } from '@mui/material';
import { Button, DatePicker, Form, Select, Space, Spin, Table, Tabs } from 'antd';
import { PieChartOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import ExportMenu, { EXPORT_FORMATS_DOCS } from '../../../Components/ExportMenu';
import ExportFileModal from '../components/ExportFileModal';
import { ExportFile, docxBlob, sectionsHtml, tableHtml, xlsxBlob } from '../services/exportFile';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { useGetAllProduits, useGetStatistiques } from '../hooks';
import { StatKpi, StatSouscriptionDifficulte, StatSyntheseProduit } from '../types';

const CHART_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4'];
const STATUT_COLORS: Record<string, string> = {
  ACTIVE: '#10b981',
  SUSPENDUE: '#f59e0b',
  RESILIEE: '#ef4444',
  ECHUE: '#64748b',
  EN_ATTENTE: '#f59e0b',
  VALIDEE: '#10b981',
  REJETEE: '#ef4444',
  ANNULEE: '#64748b',
  A_PAYER: '#3b82f6',
  PAYEE: '#10b981',
  IMPAYEE: '#ef4444',
  EN_COURS: '#f59e0b',
};
const tooltipStyle = { borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12 };

const fmtMontant = (v?: number | null) =>
  (v ?? 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
    // jsPDF ne rend pas l'espace fine insécable U+202F -> espace normale
    .replace(/[\u00A0\u202F]/g, ' ');
const fmtPct = (v?: number | null) => `${(v ?? 0).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} %`;
const colorFor = (statut: string, i: number) => STATUT_COLORS[statut] ?? CHART_COLORS[i % CHART_COLORS.length];

const KpiCard = ({ label, value, sub, color }: { label: string; value: string; sub?: string; color?: string }) => (
  <Grid item xs={12} sm={6} md={3}>
    <Paper elevation={1} sx={{ p: 2, borderRadius: 3, height: '100%', borderLeft: `4px solid ${color ?? '#116599'}` }}>
      <Typography variant="body2" color="textSecondary">{label}</Typography>
      <Typography variant="h5" fontWeight="bold">{value}</Typography>
      {sub && <Typography variant="caption" color="textSecondary">{sub}</Typography>}
    </Paper>
  </Grid>
);

const ChartCard = ({ title, subtitle, children, md = 6 }: { title: string; subtitle?: string; children: React.ReactNode; md?: number }) => (
  <Grid item xs={12} md={md}>
    <Paper elevation={1} sx={{ p: 2, borderRadius: 3, height: '100%' }}>
      <Typography variant="h6" fontWeight="bold">{title}</Typography>
      {subtitle && <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>{subtitle}</Typography>}
      {children}
    </Paper>
  </Grid>
);

type FormatExport = 'pdf' | 'xlsx' | 'docx';

const RapportPage = () => {
  const [form] = Form.useForm();
  const [periode, setPeriode] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [idProduit, setIdProduit] = useState<number | undefined>(undefined);
  const [formatExport, setFormatExport] = useState<FormatExport>('pdf');
  const [exportFile, setExportFile] = useState<ExportFile | null>(null);

  const annuler = () => {
    setPeriode(null);
    setIdProduit(undefined);
    setFormatExport('pdf');
  };

  const dateDebut = periode?.[0] ? periode[0].format('YYYY-MM-DD') : undefined;
  const dateFin = periode?.[1] ? periode[1].format('YYYY-MM-DD') : undefined;

  const { data: produits } = useGetAllProduits();
  const { data: stats, isLoading, refetch } = useGetStatistiques(dateDebut, dateFin, idProduit);

  const kpi: StatKpi | null = stats?.kpi ?? null;
  const parStatut = useMemo(() => stats?.parStatut ?? [], [stats]);
  const totalSouscriptions = useMemo(() => parStatut.reduce((acc, s) => acc + (s.Nombre ?? 0), 0), [parStatut]);

  const columnsDifficulte: ColumnsType<StatSouscriptionDifficulte> = [
    { title: 'Client', dataIndex: 'Numcli', width: 110 },
    { title: 'Nom', render: (_, r) => `${r.NOMCLI ?? ''} ${r.PRECLI ?? ''}`.trim(), ellipsis: true },
    { title: 'Produit', dataIndex: 'NomProduit', ellipsis: true },
    { title: 'Statut', dataIndex: 'Statut', width: 110 },
    { title: 'Nb impayés', dataIndex: 'NbImpayes', width: 100, align: 'right' },
    { title: 'Montant impayé', dataIndex: 'MontantImpaye', width: 130, align: 'right', render: (v) => fmtMontant(v) },
    { title: '1er impayé', dataIndex: 'PremierImpaye', width: 110, render: (v) => (v ? dayjs(v).format('DD/MM/YYYY') : '') },
  ];

  const columnsProduit: ColumnsType<StatSyntheseProduit> = [
    { title: 'Produit', dataIndex: 'NomProduit', ellipsis: true, fixed: 'left', width: 160 },
    { title: 'Souscriptions', dataIndex: 'NbSouscriptions', width: 110, align: 'right' },
    { title: 'Actives', dataIndex: 'NbActives', width: 80, align: 'right' },
    { title: 'Suspendues', dataIndex: 'NbSuspendues', width: 100, align: 'right' },
    { title: 'Résiliées', dataIndex: 'NbResiliees', width: 90, align: 'right' },
    { title: 'Échues', dataIndex: 'NbEchues', width: 80, align: 'right' },
    { title: 'Cotisations attendues', dataIndex: 'CotisationsAttendues', width: 140, align: 'right', render: (v) => fmtMontant(v) },
    { title: 'Cotisations encaissées', dataIndex: 'CotisationsEncaissees', width: 140, align: 'right', render: (v) => fmtMontant(v) },
    { title: 'Nb impayés', dataIndex: 'NbImpayes', width: 95, align: 'right' },
    { title: 'Montant impayé', dataIndex: 'MontantImpaye', width: 120, align: 'right', render: (v) => fmtMontant(v) },
    { title: 'Demandes indem.', dataIndex: 'NbDemandesIndemnisation', width: 120, align: 'right' },
    { title: 'Montant indemnisé', dataIndex: 'MontantIndemnise', width: 130, align: 'right', render: (v) => fmtMontant(v) },
    { title: 'Suspensions', dataIndex: 'NbSuspensions', width: 100, align: 'right' },
  ];

  const libellePeriode = dateDebut || dateFin
    ? `Période : ${dateDebut ? dayjs(dateDebut).format('DD/MM/YYYY') : '...'} - ${dateFin ? dayjs(dateFin).format('DD/MM/YYYY') : '...'}`
    : 'Période : toutes les données';
  const libelleProduit = idProduit ? `Produit : ${produits?.find((p: any) => p.IdProduit === idProduit)?.Nom ?? idProduit}` : 'Produit : tous';

  const handleExportPDF = () => {
    if (!stats || !kpi) return;
    const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
    doc.setFontSize(14);
    doc.text('Rapport Safe Care', 40, 40);
    doc.setFontSize(9);
    doc.text([libellePeriode, libelleProduit, `Généré le ${dayjs().format('DD/MM/YYYY HH:mm')}`], 40, 58);

    autoTable(doc, {
      startY: 100,
      head: [['Indicateur', 'Valeur']],
      body: [
        ['Souscriptions', String(kpi.NbSouscriptions)],
        ['— Actives', String(kpi.NbActives)],
        ['— Suspendues', String(kpi.NbSuspendues)],
        ['— Résiliées', String(kpi.NbResiliees)],
        ['— Échues', String(kpi.NbEchues)],
        ['Clients', String(kpi.NbClients)],
        ['Personnes couvertes', String(kpi.NbPersonnesCouvertes)],
        ['Cotisations attendues', fmtMontant(kpi.CotisationsAttendues)],
        ['Cotisations encaissées', fmtMontant(kpi.CotisationsEncaissees)],
        ['Taux de recouvrement', fmtPct(kpi.TauxRecouvrement)],
        ["Taux d'impayés", fmtPct(kpi.TauxImpayes)],
        ['Montant impayé', fmtMontant(kpi.MontantImpaye)],
        ["Demandes d'indemnisation", String(kpi.NbDemandesIndemnisation)],
        ['— en cours', String(kpi.NbDemandesEnCours)],
        ['— validées', String(kpi.NbDemandesValidees)],
        ['— rejetées', String(kpi.NbDemandesRejetees)],
        ['Montant indemnisé', fmtMontant(kpi.MontantIndemnise)],
        ['Ratio sinistralité', fmtPct(kpi.RatioSinistralite)],
        ['Suspensions', String(kpi.NbSuspensions ?? 0)],
        ['— en attente', String(kpi.NbSuspensionsEnAttente ?? 0)],
        ['— validées', String(kpi.NbSuspensionsValidees ?? 0)],
        ['Paiements effectués', String(kpi.NbPaiements ?? 0)],
        ['Cotisation moyenne', fmtMontant(kpi.CotisationMoyenne)],
        ['Taux validation indemnisations', fmtPct(kpi.TauxValidationIndemnisation)],
        ['Taux validation souscriptions', fmtPct(kpi.TauxValidationSouscription)],
      ],
      styles: { fontSize: 9 },
      headStyles: { fillColor: [17, 101, 153] },
    });

    autoTable(doc, {
      startY: (doc as any).lastAutoTable.finalY + 20,
      head: [['Statut souscription', 'Nombre', 'Cotisation']],
      body: parStatut.map((s) => [s.Statut, String(s.Nombre), fmtMontant(s.MontantCotisation)]),
      styles: { fontSize: 9 },
      headStyles: { fillColor: [17, 101, 153] },
    });

    autoTable(doc, {
      startY: (doc as any).lastAutoTable.finalY + 20,
      head: [['Événement', 'Demandes', 'Validées', 'Rejetées', 'En cours', 'Montant indemnisé']],
      body: stats.parEvenement.map((e) => [e.LibelleEvenement, String(e.Nombre), String(e.NbValidees), String(e.NbRejetees), String(e.NbEnCours), fmtMontant(e.MontantIndemnise)]),
      styles: { fontSize: 9 },
      headStyles: { fillColor: [17, 101, 153] },
    });

    const addSection = (titre: string, head: string[], body: any[][]) => {
      if (!body.length) return;
      doc.setFontSize(10);
      doc.text(titre, 40, (doc as any).lastAutoTable.finalY + 18);
      autoTable(doc, {
        startY: (doc as any).lastAutoTable.finalY + 24,
        head: [head],
        body,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [17, 101, 153] },
      });
    };

    addSection('Suspensions par statut', ['Statut', 'Nombre'], stats.suspensionsParStatut.map((s) => [s.Statut, String(s.Nombre)]));
    addSection('Suspensions par motif', ['Motif', 'Nombre'], stats.suspensionsParMotif.map((s) => [s.Motif, String(s.Nombre)]));
    addSection('Personnes couvertes par lien', ['Lien de parenté', 'Nombre'], stats.personnesParLien.map((p) => [p.LienParente, String(p.Nombre)]));
    addSection('Synthèse par produit', ['Produit', 'Souscriptions', 'Actives', 'Cotis. encaissées', 'Impayés', 'Indemnisé', 'Suspensions'],
      stats.syntheseParProduit.map((p) => [p.NomProduit, String(p.NbSouscriptions), String(p.NbActives), fmtMontant(p.CotisationsEncaissees), String(p.NbImpayes), fmtMontant(p.MontantIndemnise), String(p.NbSuspensions)]));

    if (stats.enDifficulte.length) {
      autoTable(doc, {
        startY: (doc as any).lastAutoTable.finalY + 20,
        head: [['Client', 'Nom', 'Produit', 'Statut', 'Nb impayés', 'Montant impayé']],
        body: stats.enDifficulte.map((r) => [r.Numcli, `${r.NOMCLI ?? ''} ${r.PRECLI ?? ''}`.trim(), r.NomProduit, r.Statut, String(r.NbImpayes), fmtMontant(r.MontantImpaye)]),
        styles: { fontSize: 8 },
        headStyles: { fillColor: [17, 101, 153] },
      });
    }
    setExportFile({ blob: doc.output('blob'), name: `rapport_safecare_${dayjs().format('YYYYMMDD_HHmm')}.pdf`, ext: 'pdf' });
  };

  const handleExportExcel = () => {
    if (!stats || !kpi) return;
    const sheets = [
      { name: 'KPI', data: [kpi] },
      { name: 'Souscriptions statut', data: stats.parStatut },
      { name: 'Par produit', data: stats.parProduit },
      { name: 'Demandes souscription', data: stats.demandesSouscriptionParStatut },
      { name: 'Indemnisations', data: stats.parEvenement },
      { name: 'Echeances', data: stats.echeancesParStatut },
      { name: 'Tendance', data: stats.tendance },
      { name: 'Suspensions statut', data: stats.suspensionsParStatut },
      { name: 'Suspensions motif', data: stats.suspensionsParMotif },
      { name: 'Personnes lien', data: stats.personnesParLien },
      { name: 'Synthese produit', data: stats.syntheseParProduit },
      { name: 'En difficulte', data: stats.enDifficulte },
    ];
    setExportFile({
      blob: xlsxBlob(sheets),
      name: `rapport_safecare_${dayjs().format('YYYYMMDD_HHmm')}.xlsx`,
      ext: 'xlsx',
      previewHtml: `<h3>Rapport Safe Care</h3>${sheets.map((s) => `<h4>${s.name}</h4>${tableHtml(s.data)}`).join('')}`,
    });
  };

  const lignesKpi = () => {
    if (!kpi || !stats) return [];
    return [
      { Indicateur: 'Période', Valeur: libellePeriode.replace('Période : ', '') },
      { Indicateur: 'Produit', Valeur: libelleProduit.replace('Produit : ', '') },
      { Indicateur: 'Souscriptions', Valeur: String(kpi.NbSouscriptions) },
      { Indicateur: '— Actives', Valeur: String(kpi.NbActives) },
      { Indicateur: '— Suspendues', Valeur: String(kpi.NbSuspendues) },
      { Indicateur: '— Résiliées', Valeur: String(kpi.NbResiliees) },
      { Indicateur: '— Échues', Valeur: String(kpi.NbEchues) },
      { Indicateur: 'Clients', Valeur: String(kpi.NbClients) },
      { Indicateur: 'Personnes couvertes', Valeur: String(kpi.NbPersonnesCouvertes) },
      { Indicateur: 'Cotisations attendues', Valeur: fmtMontant(kpi.CotisationsAttendues) },
      { Indicateur: 'Cotisations encaissées', Valeur: fmtMontant(kpi.CotisationsEncaissees) },
      { Indicateur: 'Taux de recouvrement', Valeur: fmtPct(kpi.TauxRecouvrement) },
      { Indicateur: 'Taux d\'impayés', Valeur: fmtPct(kpi.TauxImpayes) },
      { Indicateur: 'Montant impayé', Valeur: fmtMontant(kpi.MontantImpaye) },
      { Indicateur: "Demandes d'indemnisation", Valeur: String(kpi.NbDemandesIndemnisation) },
      { Indicateur: '— en cours', Valeur: String(kpi.NbDemandesEnCours) },
      { Indicateur: '— validées', Valeur: String(kpi.NbDemandesValidees) },
      { Indicateur: '— rejetées', Valeur: String(kpi.NbDemandesRejetees) },
      { Indicateur: 'Montant indemnisé', Valeur: fmtMontant(kpi.MontantIndemnise) },
      { Indicateur: 'Ratio sinistralité', Valeur: fmtPct(kpi.RatioSinistralite) },
      { Indicateur: 'Suspensions', Valeur: String(kpi.NbSuspensions ?? 0) },
      { Indicateur: '— en attente', Valeur: String(kpi.NbSuspensionsEnAttente ?? 0) },
      { Indicateur: '— validées', Valeur: String(kpi.NbSuspensionsValidees ?? 0) },
      { Indicateur: 'Paiements effectués', Valeur: String(kpi.NbPaiements ?? 0) },
      { Indicateur: 'Cotisation moyenne', Valeur: fmtMontant(kpi.CotisationMoyenne) },
      { Indicateur: 'Taux validation indemnisations', Valeur: fmtPct(kpi.TauxValidationIndemnisation) },
      { Indicateur: 'Taux validation souscriptions', Valeur: fmtPct(kpi.TauxValidationSouscription) },
      ...parStatut.map((s) => ({ Indicateur: `Souscriptions ${s.Statut}`, Valeur: String(s.Nombre) })),
      ...(stats.parProduit ?? []).map((p) => ({ Indicateur: `Produit ${p.NomProduit}`, Valeur: String(p.Nombre) })),
      ...(stats.suspensionsParStatut ?? []).map((s) => ({ Indicateur: `Suspensions ${s.Statut}`, Valeur: String(s.Nombre) })),
      ...(stats.suspensionsParMotif ?? []).map((s) => ({ Indicateur: `Motif suspension : ${s.Motif}`, Valeur: String(s.Nombre) })),
      ...(stats.personnesParLien ?? []).map((p) => ({ Indicateur: `Personnes couvertes - ${p.LienParente}`, Valeur: String(p.Nombre) })),
      ...(stats.syntheseParProduit ?? []).flatMap((p) => [
        { Indicateur: `${p.NomProduit} : souscriptions`, Valeur: String(p.NbSouscriptions) },
        { Indicateur: `${p.NomProduit} : cotisations encaissées`, Valeur: fmtMontant(p.CotisationsEncaissees) },
        { Indicateur: `${p.NomProduit} : montant indemnisé`, Valeur: fmtMontant(p.MontantIndemnise) },
      ]),
    ];
  };

  const handleExportWord = async () => {
    if (!stats || !kpi) return;
    const titre = `Rapport Safe Care - ${libellePeriode} - ${libelleProduit}`;
    const sections = [
      { titre: 'Indicateurs clés', data: lignesKpi() },
      { titre: 'Souscriptions par statut', data: parStatut.map((s) => ({ Statut: s.Statut, Nombre: s.Nombre, Cotisation: fmtMontant(s.MontantCotisation) })) },
      { titre: 'Répartition par produit', data: stats.parProduit.map((p) => ({ Produit: p.NomProduit, Nombre: p.Nombre })) },
      { titre: 'Demandes de souscription par statut', data: stats.demandesSouscriptionParStatut.map((s) => ({ Statut: s.Statut, Nombre: s.Nombre })) },
      {
        titre: 'Indemnisations par événement',
        data: stats.parEvenement.map((e) => ({ Événement: e.LibelleEvenement, Demandes: e.Nombre, Validées: e.NbValidees, 'En cours': e.NbEnCours, Rejetées: e.NbRejetees, 'Montant indemnisé': fmtMontant(e.MontantIndemnise) })),
      },
      {
        titre: 'Échéances par statut',
        data: stats.echeancesParStatut.map((e) => ({ Statut: e.Statut, Nombre: e.Nombre, 'Montant dû': fmtMontant(e.Montant), 'Montant payé': fmtMontant(e.MontantPaye) })),
      },
      { titre: 'Suspensions par statut', data: stats.suspensionsParStatut.map((s) => ({ Statut: s.Statut, Nombre: s.Nombre })) },
      { titre: 'Suspensions par motif', data: stats.suspensionsParMotif.map((s) => ({ Motif: s.Motif, Nombre: s.Nombre })) },
      { titre: 'Personnes couvertes par lien', data: stats.personnesParLien.map((p) => ({ 'Lien de parenté': p.LienParente, Nombre: p.Nombre })) },
      {
        titre: 'Synthèse par produit',
        data: stats.syntheseParProduit.map((p) => ({
          Produit: p.NomProduit, Souscriptions: p.NbSouscriptions, Actives: p.NbActives, 'Cotis. encaissées': fmtMontant(p.CotisationsEncaissees),
          'Nb impayés': p.NbImpayes, 'Montant impayé': fmtMontant(p.MontantImpaye), 'Montant indemnisé': fmtMontant(p.MontantIndemnise), Suspensions: p.NbSuspensions,
        })),
      },
      {
        titre: 'Contrats en difficulté',
        data: stats.enDifficulte.map((r) => ({
          'N°': r.IdSouscription, Client: r.Numcli, Nom: `${r.NOMCLI ?? ''} ${r.PRECLI ?? ''}`.trim(), Produit: r.NomProduit,
          Statut: r.Statut, 'Nb impayés': r.NbImpayes, 'Montant impayé': fmtMontant(r.MontantImpaye), '1er impayé': r.PremierImpaye ? dayjs(r.PremierImpaye).format('DD/MM/YYYY') : '',
        })),
      },
    ];
    const blob = await docxBlob(titre, sections);
    setExportFile({
      blob,
      name: `rapport_safecare_${dayjs().format('YYYYMMDD_HHmm')}.docx`,
      ext: 'docx',
      previewHtml: `<h3>${titre}</h3>${sectionsHtml(sections)}`,
    });
  };

  // Un seul bouton "Exporter" : génère le fichier puis affiche la carte animée
  // (Visualiser / Plus tard) au lieu de télécharger directement.
  const handleExport = (fmt: FormatExport) => {
    setFormatExport(fmt);
    if (!stats || !kpi) return;
    if (fmt === 'xlsx') handleExportExcel();
    else if (fmt === 'docx') handleExportWord();
    else handleExportPDF();
  };

  return (
    <div className="tab safeCarePage">
      <Typography align="center" className="formTitle">
        <b>SAFE CARE - RAPPORT</b>
      </Typography>
      <Spin spinning={isLoading} size="large" style={{ color: '#116599' }}>
        <Form autoComplete="off" labelAlign="left" form={form} style={{ minWidth: 'auto', maxWidth: 'auto' }}>

          {/* Barre d'action charte (.bG) : toutes les actions se gèrent ici */}
          <div className="bG">
            <legend>
              <Typography color="#116599">Actions</Typography>
            </legend>
            <Form.Item shouldUpdate className="bouttong">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button size="large" className="bt_add" type="ghost" htmlType="button" block onClick={() => refetch()}>
                  Actualiser
                </Button>
                <ExportMenu disabled={!kpi} onExport={(f) => handleExport(f as FormatExport)} formats={EXPORT_FORMATS_DOCS} />
                <Button size="large" className="bt_add" type="ghost" htmlType="reset" block onClick={annuler}>
                  Annuler
                </Button>
              </Space>
            </Form.Item>
          </div>

          <div className="clientP">
            <div>
              <Tabs
                size="small"
                type="card"
                defaultActiveKey="1"
                items={[{
                  key: '1',
                  label: (
                    <span id="Tab">
                      <PieChartOutlined /> Rapport
                    </span>
                  ),
                  children: (
                <div className="tabItem" style={{ height: '780px', display: 'block' }}>
                  <div style={{ width: '85%', height: '100%', overflowY: 'auto', overflowX: 'hidden', paddingRight: 8 }}>
                  {/* Filtres */}
                  <Box fontStyle="inherit" className="border border-left" sx={{ p: 2, mb: 2 }}>
                    <legend>Filtres</legend>
                    <Space wrap>
                      <DatePicker.RangePicker
                        format="DD/MM/YYYY"
                        value={periode as any}
                        onChange={(v) => setPeriode(v as any)}
                        placeholder={['Date début', 'Date fin']}
                      />
                      <Select
                        allowClear
                        placeholder="Tous les produits"
                        style={{ minWidth: 220 }}
                        value={idProduit}
                        onChange={(v) => setIdProduit(v)}
                        options={(produits ?? []).map((p: any) => ({ value: p.IdProduit, label: `${p.Code} - ${p.Nom}` }))}
                      />
                    </Space>
                  </Box>

                  <Spin spinning={isLoading} size="large">
                    <Grid container spacing={2}>
            {/* 1. Diagramme circulaire : répartition des souscriptions par statut (en premier) */}
            <ChartCard title="Répartition des souscriptions par statut" subtitle={`${totalSouscriptions} souscription(s) - ${libellePeriode}`} md={6}>
              <ResponsiveContainer width="100%" height={320}>
                <PieChart>
                  <Pie
                    data={parStatut}
                    dataKey="Nombre"
                    nameKey="Statut"
                    cx="50%"
                    cy="50%"
                    innerRadius={70}
                    outerRadius={115}
                    paddingAngle={2}
                    label={(e: any) => `${e.Statut} (${totalSouscriptions ? Math.round((e.Nombre / totalSouscriptions) * 100) : 0}%)`}
                  >
                    {parStatut.map((s, i) => <Cell key={s.Statut} fill={colorFor(s.Statut, i)} />)}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Répartition par produit" subtitle="Nombre de souscriptions par produit Safe Care" md={6}>
              <ResponsiveContainer width="100%" height={320}>
                <PieChart>
                  <Pie data={stats?.parProduit ?? []} dataKey="Nombre" nameKey="NomProduit" cx="50%" cy="50%" innerRadius={70} outerRadius={115} paddingAngle={2}>
                    {(stats?.parProduit ?? []).map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>

            {/* 2. KPI */}
            <KpiCard label="Souscriptions actives" value={String(kpi?.NbActives ?? 0)} sub={`${kpi?.NbSouscriptions ?? 0} au total - ${kpi?.NbClients ?? 0} client(s)`} color="#10b981" />
            <KpiCard label="Cotisations encaissées" value={fmtMontant(kpi?.CotisationsEncaissees)} sub={`Attendues : ${fmtMontant(kpi?.CotisationsAttendues)} - Recouvrement ${fmtPct(kpi?.TauxRecouvrement)}`} color="#3b82f6" />
            <KpiCard label="Taux d'impayés" value={fmtPct(kpi?.TauxImpayes)} sub={`${kpi?.NbEcheancesImpayees ?? 0} échéance(s) - ${fmtMontant(kpi?.MontantImpaye)}`} color="#ef4444" />
            <KpiCard label="Ratio sinistralité" value={fmtPct(kpi?.RatioSinistralite)} sub={`Indemnisé : ${fmtMontant(kpi?.MontantIndemnise)}`} color="#8b5cf6" />
            <KpiCard label="Indemnisations en cours" value={String(kpi?.NbDemandesEnCours ?? 0)} sub={`${kpi?.NbDemandesValidees ?? 0} validées - ${kpi?.NbDemandesRejetees ?? 0} rejetées`} color="#f59e0b" />
            <KpiCard label="Souscriptions en attente" value={String(kpi?.NbDemandesSouscriptionEnAttente ?? 0)} sub={`Délai moyen validation : ${(kpi?.DelaiMoyenValidationSouscriptionJours ?? 0).toFixed(1)} j`} color="#06b6d4" />
            <KpiCard label="Personnes couvertes" value={String(kpi?.NbPersonnesCouvertes ?? 0)} sub={`Suspendues ${kpi?.NbSuspendues ?? 0} - Résiliées ${kpi?.NbResiliees ?? 0} - Échues ${kpi?.NbEchues ?? 0}`} color="#64748b" />
            <KpiCard label="Suspensions" value={String(kpi?.NbSuspensions ?? 0)} sub={`${kpi?.NbSuspensionsEnAttente ?? 0} en attente - ${kpi?.NbSuspensionsValidees ?? 0} validée(s)`} color="#f59e0b" />
            {/* Paiements et validation : tableau récapitulatif détaillé */}
            <ChartCard title="Paiements, validation et personnes couvertes" subtitle="Activité d'encaissement, taux de traitement des demandes et répartition par lien de parenté" md={12}>
              <Table
                size="small"
                showHeader={false}
                pagination={false}
                columns={[
                  { title: 'Indicateur', dataIndex: 'label', width: '30%', render: (v: string) => <b>{v}</b> },
                  { title: 'Valeur', dataIndex: 'value', width: '20%', align: 'right', render: (v: string) => <b style={{ color: '#116599' }}>{v}</b> },
                  { title: 'Détail', dataIndex: 'detail' },
                ]}
                dataSource={[
                  { key: '1', label: 'Paiements de cotisation effectués', value: String(kpi?.NbPaiements ?? 0), detail: `Cotisation moyenne encaissée : ${fmtMontant(kpi?.CotisationMoyenne)}` },
                  { key: '2', label: 'Taux de validation des indemnisations', value: fmtPct(kpi?.TauxValidationIndemnisation), detail: `${kpi?.NbDemandesValidees ?? 0} validée(s), ${kpi?.NbDemandesEnCours ?? 0} en cours, ${kpi?.NbDemandesRejetees ?? 0} rejetée(s)` },
                  { key: '3', label: 'Taux de validation des souscriptions', value: fmtPct(kpi?.TauxValidationSouscription), detail: `${kpi?.NbDemandesSouscriptionEnAttente ?? 0} demande(s) en attente de traitement` },
                  { key: '4', label: 'Personnes couvertes', value: String(kpi?.NbPersonnesCouvertes ?? 0), detail: 'Répartition par lien de parenté ci-dessous' },
                  ...(stats?.personnesParLien ?? []).map((p, i) => ({
                    key: `l${i}`,
                    label: `— ${p.LienParente}`,
                    value: String(p.Nombre),
                    detail: `${kpi?.NbPersonnesCouvertes ? Math.round((p.Nombre / kpi.NbPersonnesCouvertes) * 100) : 0} % des personnes couvertes`,
                  })),
                ]}
              />
            </ChartCard>

            {/* 3. Graphiques complémentaires */}
            <ChartCard title="Évolution mensuelle" subtitle="Cotisations attendues / encaissées et montants indemnisés">
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={stats?.tendance ?? []} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="Mois" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => fmtMontant(v)} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v: any) => fmtMontant(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Area type="monotone" dataKey="CotisationsAttendues" name="Attendues" stroke="#94a3b8" fill="#94a3b8" fillOpacity={0.15} />
                  <Area type="monotone" dataKey="CotisationsEncaissees" name="Encaissées" stroke="#10b981" fill="#10b981" fillOpacity={0.25} />
                  <Area type="monotone" dataKey="MontantIndemnise" name="Indemnisé" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.25} />
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Nouvelles souscriptions et impayés" subtitle="Par mois">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={stats?.tendance ?? []} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="Mois" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="NbSouscriptions" name="Souscriptions" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="NbImpayes" name="Impayés" fill="#ef4444" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="NbDemandesIndemnisation" name="Demandes indemn." fill="#f59e0b" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Indemnisations par événement couvert" subtitle="Nombre de demandes et montant indemnisé">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={stats?.parEvenement ?? []} layout="vertical" margin={{ top: 10, right: 20, left: 60, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                  <YAxis type="category" dataKey="LibelleEvenement" tick={{ fontSize: 11 }} width={120} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="NbValidees" name="Validées" stackId="a" fill="#10b981" />
                  <Bar dataKey="NbEnCours" name="En cours" stackId="a" fill="#f59e0b" />
                  <Bar dataKey="NbRejetees" name="Rejetées" stackId="a" fill="#ef4444" />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Demandes de souscription" subtitle="Répartition par statut">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={stats?.demandesSouscriptionParStatut ?? []} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="Statut" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Bar dataKey="Nombre" name="Demandes" radius={[4, 4, 0, 0]}>
                    {(stats?.demandesSouscriptionParStatut ?? []).map((s, i) => <Cell key={s.Statut} fill={colorFor(s.Statut, i)} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Échéances par statut" subtitle="Nombre et montants">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={stats?.echeancesParStatut ?? []} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="Statut" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => fmtMontant(v)} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v: any) => fmtMontant(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="Montant" name="Montant dû" fill="#94a3b8" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="MontantPaye" name="Montant payé" fill="#10b981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Montant indemnisé par événement" subtitle="Demandé vs indemnisé">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={stats?.parEvenement ?? []} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="LibelleEvenement" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => fmtMontant(v)} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(v: any) => fmtMontant(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="MontantDemande" name="Demandé" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="MontantIndemnise" name="Indemnisé" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Suspensions" subtitle="Demandes de suspension par statut">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={stats?.suspensionsParStatut ?? []} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="Statut" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Bar dataKey="Nombre" name="Demandes" radius={[4, 4, 0, 0]}>
                    {(stats?.suspensionsParStatut ?? []).map((s, i) => <Cell key={s.Statut} fill={colorFor(s.Statut, i)} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Motifs de suspension" subtitle="Top motifs déclarés">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={stats?.suspensionsParMotif ?? []} layout="vertical" margin={{ top: 10, right: 20, left: 60, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                  <YAxis type="category" dataKey="Motif" tick={{ fontSize: 10 }} width={150} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Bar dataKey="Nombre" name="Suspensions" fill="#f59e0b" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            {/* 4. Synthèse consolidée par produit : toutes les données, lecture directe */}
            <ChartCard title="Synthèse par produit" subtitle="Souscriptions, cotisations, impayés, indemnisations et suspensions par produit" md={12}>
              <Table
                size="small"
                rowKey="IdProduit"
                columns={columnsProduit}
                dataSource={stats?.syntheseParProduit ?? []}
                pagination={false}
                scroll={{ x: 1100 }}
              />
            </ChartCard>

            <ChartCard title="Souscriptions en difficulté" subtitle="Top 20 des contrats avec échéances impayées" md={12}>
              <Table
                size="small"
                rowKey="IdSouscription"
                columns={columnsDifficulte}
                dataSource={stats?.enDifficulte ?? []}
                pagination={{ pageSize: 10, showSizeChanger: false }}
                scroll={{ x: 800 }}
              />
            </ChartCard>
                    </Grid>
                  </Spin>
                  </div>
                </div>
              ),
            }]}
          />
            </div>
          </div>
        </Form>
      </Spin>
      <ExportFileModal file={exportFile} onDone={() => setExportFile(null)} />
    </div>
  );
};

export default RapportPage;
